import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Repository Resolver Context
 *
 * Provides context-aware repository resolution via React Context.
 * Connects to SessionProvider to get currentOrganizationId and maps it to PathContext.
 * Uses centralized repositoryResolver from data-access.
 */
import { createContext, useContext, useState, useEffect, useCallback, useMemo, } from 'react';
import { repositoryResolver } from '@rammetec/data-access';
import { useSessionManager } from '../hooks/useSessionManager.js';
const RepositoryResolverContext = createContext(null);
export const RepositoryResolverProvider = ({ children }) => {
    const { currentOrganizationId } = useSessionManager();
    const [configsLoading, setConfigsLoading] = useState(false);
    const [configsError, setConfigsError] = useState(null);
    // Build context from organization ID (can be extended for other context keys)
    const currentContext = useMemo(() => {
        if (!currentOrganizationId)
            return {};
        return { orgId: currentOrganizationId };
    }, [currentOrganizationId]);
    // Set context when organization changes
    useEffect(() => {
        let cancelled = false;
        setConfigsLoading(true);
        setConfigsError(null);
        repositoryResolver
            .setContext(currentContext)
            .then(() => {
            if (!cancelled) {
                // Configs loaded successfully
            }
        })
            .catch((error) => {
            if (!cancelled) {
                setConfigsError(error);
            }
        })
            .finally(() => {
            if (!cancelled) {
                setConfigsLoading(false);
            }
        });
        return () => {
            cancelled = true;
        };
    }, [currentContext]);
    const refreshConfigs = useCallback(async () => {
        if (!currentOrganizationId)
            return;
        const contextKey = `orgId:${currentOrganizationId}`;
        repositoryResolver.invalidateContext(contextKey);
        setConfigsLoading(true);
        try {
            await repositoryResolver.loadConfigs(contextKey, { orgId: currentOrganizationId });
            setConfigsError(null);
        }
        catch (error) {
            setConfigsError(error);
        }
        finally {
            setConfigsLoading(false);
        }
    }, [currentOrganizationId]);
    const invalidateContext = useCallback((contextKey) => {
        repositoryResolver.invalidateContext(contextKey);
    }, []);
    // Backward compatibility alias
    const invalidateCache = useCallback((orgId) => {
        if (orgId) {
            repositoryResolver.invalidateContext(`orgId:${orgId}`);
        }
        else {
            repositoryResolver.invalidateContext();
        }
    }, []);
    const isReady = !configsLoading;
    const value = useMemo(() => ({
        isReady,
        currentContext,
        currentOrgId: currentOrganizationId,
        configsLoading,
        configsError,
        refreshConfigs,
        invalidateCache,
        invalidateContext,
    }), [
        isReady,
        currentContext,
        currentOrganizationId,
        configsLoading,
        configsError,
        refreshConfigs,
        invalidateCache,
        invalidateContext,
    ]);
    return (_jsx(RepositoryResolverContext.Provider, { value: value, children: children }));
};
export const useRepositoryResolver = () => {
    const context = useContext(RepositoryResolverContext);
    if (!context) {
        throw new Error('useRepositoryResolver must be used within RepositoryResolverProvider');
    }
    return context;
};
//# sourceMappingURL=RepositoryResolverContext.js.map